/* eslint-disable import/no-unresolved */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import {
  BadgeCheck,
  Star,
  Award,
  Users,
  Globe,
  Users2,
  Zap,
  FileBadge,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ProfileInfoDataInterface } from "@/modules/Astro/UserProfile/ProfilesInfo/profileTypes";
import styles from "./StickyProfileHero.module.scss";

function formatNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(num);
}

function hashToRange(str: string, min: number, max: number): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return min + (Math.abs(hash) % (max - min + 1));
}

interface StickyProfileHeroProps {
  collapsed?: boolean;
  cardRef?: React.RefObject<HTMLElement>;
  userData?: ProfileInfoDataInterface;
}

export default function StickyProfileHero({
  collapsed = false,
  cardRef,
  userData,
}: StickyProfileHeroProps) {
  const profile = userData?.profile;
  const rnr = userData?.rnr;
  const userMeta = userData?.userMeta;

  const name = profile?.name ?? "";
  const profilePic = profile?.profilePic ?? "";
  const isVerified = profile?.isVerified ?? false;
  const isOnline = profile?.status === "online";
  const avgRating =
    profile?.avgRating?.averageRating ?? rnr?.rating?.averageRating ?? "0";
  const totalRatings =
    rnr?.rating?.totalRatings ?? profile?.avgRating?.totalRatings ?? 0;
  const yearsOfExperience =
    Math.max(0, ...(profile?.creatorCategories?.map((c) => c.experience ?? 0) ?? [0]));
  const preferredLanguage = profile?.preferredLanguage ?? [];
  const speciality =
    profile?.creatorCategories
      ?.flatMap((c) => c.subCategories)
      .map((s) => s.name)
      .join(" • ") ?? "";
  const followersCount = userMeta?.followersCount ?? 0;
  const expert = profile?.expert;

  const profileMeta = [
    avgRating !== "0" && {
      chip: `${avgRating} / 5`,
      icon: Star,
      iconColor: "#eab308",
      highlight: true,
    },
    {
      chip: `${yearsOfExperience} yrs experience`,
      icon: Award,
      iconColor: "#8b5cf6",
    },
    {
      chip: `${formatNumber(totalRatings)} consults`,
      icon: Users,
      iconColor: "#10b981",
    },
    preferredLanguage.length > 0 && {
      chip: preferredLanguage.join(" · "),
      icon: Globe,
      iconColor: "#0284c7",
    },
    {
      chip: `Avg reply 30s`,
      icon: Zap,
      iconColor: "#f97316",
    },
    expert && {
      chip: expert.title,
      icon: FileBadge,
      iconColor: "#6366f1",
    },
  ].filter(Boolean) as Array<{
    chip: string;
    icon: LucideIcon;
    iconColor: string;
    highlight?: boolean;
  }>;

  const statistics = [
    {
      label: "RATING",
      value: avgRating !== "0" ? avgRating : "0",
      icon: Star,
      iconColor: "#eab308",
    },
    {
      label: "CONSULTS",
      value: totalRatings > 0 ? formatNumber(totalRatings) : "0",
      icon: Users,
      iconColor: "#10b981",
    },
    {
      label: "PRACTICE",
      value:
        yearsOfExperience > 0
          ? `${yearsOfExperience}y`
          : String(hashToRange(profile?.username ?? profile?.userId ?? "user", 1000, 1500)),
      icon: Award,
      iconColor: "#8b5cf6",
    },
    {
      label: "FOLLOWERS",
      value:
        followersCount > 0
          ? formatNumber(followersCount)
          : String(hashToRange(profile?.username ?? profile?.userId ?? "user", 1000, 1500)),
      icon: Users2,
      iconColor: "#3b82f6",
    },
  ];

  return (
    <section
      ref={cardRef}
      className={`${styles.card} ${collapsed ? styles.collapsed : ""}`}
    >
      <div className={styles.header}>
        <div className={styles.avatarWrapper}>
          <div className={styles.avatar}>
            {profilePic ? <img src={profilePic} alt={name} /> : name.charAt(0)}
          </div>

          {true && <span className={styles.onlineIndicator} />}
        </div>

        <div className={styles.content}>
          <div className={styles.title}>
            <h2>{name || "—"}</h2>

            {true && (
              <span className={styles.verified}>
                <BadgeCheck size={15} />
                Verified
              </span>
            )}
          </div>

          {speciality && <p className={styles.speciality}>{speciality}</p>}

          <div className={styles.status}>
            <span className={styles.statusDot} />
            <span className={styles.onlineText}>{"Online now"}</span>
          </div>
        </div>
      </div>

      {profileMeta.length > 0 && (
        <div className={styles.tags}>
          {profileMeta.map((item, index) => {
            const Icon = item.icon;

            return (
              <div
                key={index}
                className={`${styles.tag} ${
                  item.highlight ? styles.highlightTag : ""
                }`}
              >
                {Icon && (
                  <Icon
                    size={14}
                    style={{ color: item.iconColor }}
                    className={
                      item.highlight ? styles.starIcon : styles.tagIcon
                    }
                    fill={item.highlight ? "currentColor" : "none"}
                  />
                )}
                <span>{item.chip}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.divider} />
      <div className={styles.statisticsGrid}>
        {statistics.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className={styles.statCard}>
              {Icon && (
                <div
                  className={styles.statIcon}
                  style={{
                    color: item.iconColor,
                    backgroundColor: `${item.iconColor}15`,
                    boxShadow: `0 0 0 1.5px ${item.iconColor}50`,
                  }}
                >
                  <Icon size={16} />
                </div>
              )}
              <h3>{item.value}</h3>
              <p>{item.label}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
